import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from 'react-select';
import Mustache from "mustache";
import { useErrorBoundary } from "react-error-boundary";
import { getBatchPrintZipCodes, getBatchPrintHCPs, getBatchPrintPrescriber } from "../API/batchPrint";
import { getConfigValue, getConfigCollection } from "../util/getConfigValue";
import { AppContext } from "../util/AppContext";
import { ProductsModal, ProductsModalButton } from "../components/ProductsModal";
import MessageDetailsModal from "../components/MessageDetailsModal";
import PrintOptionDropdown from "../components/printOptions/printOptionDropdown";
import CategoryDropdown from "../components/CategoryDropdown";
import BatchPrintList from "../components/BatchPrintList";
import ToastAlert from "../components/ToastAlert";
import { batchPrintDocumentGenerate, documentGenerationType } from "../API/documentGenerate";
import PreviewModal from '../components/PreviewModal';
import TargetModal from "../components/modals/TargetModal";
import { focusTypeName, hcpType } from "../util/constants";
import { isDefinedNumber } from "../util/validation";
import { useRoutingValidation } from "../util/useRoutingValidation";
import FavoriteFilter from "../components/FavoriteFilter";
import { useBreadcrumbs } from '../util/BreadcrumbContext';
import CreateSalesAidStepProgressBar from "../components/CreateSalesAidStepProgressBar";
import { SalesAidStepIds } from "../util/salesAidNavigation";

function SelectTargetBatchPage() {
    const [appState, setAppState] = useContext(AppContext);
    const { showBoundary } = useErrorBoundary();
    const navigate = useNavigate();

    const [productImageSource, setProductImageSource] = useState(appState?.currentProduct?.imagePath);
    const [currentProduct, setCurrentProduct] = useState(appState?.currentProduct);
    const [pageCategory, setPageCategory] = useState(appState.category);
    const [batchPrintCategories, setBatchPrintCategories] = useState(appState?.batchPrintCategories);
    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearchInput, setDebouncedSearchInput] = useState("");
    const [selectedColumnState, setSelectedColumnState] = useState([]);
    const [zipCodes, setZipCodes] = useState([]);
    const [hcpsBatchResponses, setHcpsBatchResponses] = useState();
    const [selectedZipCodes, setSelectedZipCodes] = useState([]);
    const [filteredBatchRows, setFilteredBatchRows] = useState([]);
    const [prescriberData, setPrescriberData] = useState([]);
    const [maxDisplayColumns, setMaxDisplayColumns] = useState(5);
    const [exceedMaxColumns, setExceedMaxColumns] = useState(false);

    const minPrintColumns = getConfigValue(appState.config.PlanList.MinPrintableColumns, appState)?.Value || 3;
    const maxPrintColumns = getConfigValue(appState.config.PlanList.MaxPrintableColumns, appState)?.Value || 5;
    const maxSelectedBatch = getConfigValue(appState.config.BatchPrint?.Page?.MaxNumber?.Print, appState)?.Value || 10;
    const [selectedBatch, setSelectedBatch] = useState([]);

    const [noResultsFound, setNoResultsFound] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [showMessageDetailsModal, setShowMessageDetailsModal] = useState(false);
    const [showProductsModal, setShowProductsModal] = useState(false);
    const [isConfigMenuVisible, setIsConfigMenuVisible] = useState(false);
    const hcpNameRequired = getConfigValue(appState.config?.PrintOptions?.HCPName?.Required, appState)?.Value === "true";
    const ehrBackPageRequired = getConfigValue(appState.config?.PrintOptions?.EhrBackPage?.Required, appState)?.Value === "true";
    const batchCoverPageRequired = getConfigValue(appState.config?.PrintOptions?.BatchPrint?.CoverPage?.Required, appState)?.Value === "true";
    const savingsCardRequired = getConfigValue(appState.config?.PrintOptions?.SavingsCard?.Required, appState)?.Value === "true";
    const salesRepInfoRequired = getConfigValue(appState.config?.PrintOptions?.SalesRepInfo?.Required, appState)?.Value === "true";
    const frmInfoRequired = getConfigValue(appState.config?.PrintOptions?.FRMInfo?.Required, appState)?.Value === "true";
    const [configTargetNameisChecked, setConfigTargetNameisChecked] = useState(hcpNameRequired || getConfigValue(appState.config?.PrintOptions?.HCPName?.Default, appState)?.Value === "true");
    const [configEHRisChecked, setConfigEHRisChecked] = useState(ehrBackPageRequired || getConfigValue(appState.config?.PrintOptions?.EhrBackPage?.Default, appState)?.Value === "true");;

    const configMenuRef = useRef(null);
    const configMenuContentRef = useRef(null);

    const [showToast, setShowToast] = useState(false);
    const [toastTitle, setToastTitle] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [toastAutohide, setToastAutohide] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const groupPracticeEnabled = getConfigValue(appState.config.GroupPractice?.Enabled, appState)?.Value === "true";
    const printOptionsEhrBackPageEnabled = getConfigValue(appState.config.PrintOptions?.EhrBackPage?.Enabled, appState)?.Value === "true";
    const printOptionsCoverPageEnabled = getConfigValue(appState.config?.PrintOptions?.BatchPrint?.CoverPage?.Enabled, appState)?.Value === "true";
    const [printOptionsCoverPageIsChecked, setPrintOptionsCoverPageIsChecked] = useState(batchCoverPageRequired || getConfigValue(appState.config?.PrintOptions?.BatchPrint?.CoverPage?.Default, appState)?.Value === "true")
    const batchPrintConfig = useMemo(() => getConfigCollection(appState.config?.BatchPrint, appState, 'BatchPrint'), [appState]);
    const [savingsCardIsChecked, setSavingsCardIsChecked] = useState(savingsCardRequired || getConfigValue(appState.config?.PrintOptions?.SavingsCard?.Default, appState)?.Value === "true");
    const savingsCardIsEnabled = getConfigValue(appState.config?.PrintOptions?.SavingsCard?.Enabled, appState)?.Value === "true";
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [pdfFileName, setPdfFileName] = useState('');
    const [pdfUrl, setPdfUrl] = useState('');
    const printOptionsSelectSalesRepInfoEnabled = getConfigValue(appState.config.PrintOptions?.SalesRepInfo?.Enabled, appState)?.Value === 'true';
    const printOptionsSelectFRMInfoEnabled = getConfigValue(appState.config.PrintOptions?.FRMInfo?.Enabled, appState)?.Value === 'true';
    const hcpFavoritesEnabled = getConfigValue(appState.config.HCP?.Favorites?.Enabled, appState)?.Value === 'true';
    const [hcpCheckbox, setHcpCheckbox] = useState(true);
    const [hcpFavoriteCheckbox, setHcpFavoriteCheckbox] = useState(getConfigValue(appState.config.HCP?.Favorites?.Filter?.Default, appState)?.Value === 'true');
    const [groupPracticeCheckbox, setGroupPracticeCheckbox] = useState(true);
    const [batchPageResetTrigger, setBatchPageResetTrigger] = useState(0);
    const isFavoriteUpdateRef = useRef(false);
    const [maxRecordsMessage, setMaxRecordsMessage] = useState(null);

    const [printOptionsSalesRepInfoIsChecked, setPrintOptionsSalesRepInfoIsChecked] = useState(
        salesRepInfoRequired || getConfigValue(appState.config?.PrintOptions?.SalesRepInfo?.Default, appState)?.Value === 'true'
    );

    const [printOptionsFRMInfoInfoIsChecked, setPrintOptionsFRMInfoInfoIsChecked] = useState(
        frmInfoRequired || getConfigValue(appState.config?.PrintOptions?.FRMInfo?.Default, appState)?.Value === 'true'
    );

    const [showTargetModal, setShowTargetModal] = useState(false);

    const isValid = selectedBatch.length >= 1 && selectedBatch.length <= maxSelectedBatch && selectedColumnState.length >= minPrintColumns;
    const {setBreadcrumbs } = useBreadcrumbs();
    let maxRecords = 100;
	if (getConfigValue(appState?.config?.BatchPrint?.MaximumHCPsList, appState)?.Value) {
		maxRecords = Number.parseInt(getConfigValue(appState?.config?.BatchPrint?.MaximumHCPsList, appState)?.Value, 10);
	}

    useEffect(() => {
        setBreadcrumbs([
            { label: getConfigValue(appState.config?.Focus?.Header, appState)?.Value, path: "/SelectTargetBatchPage"}
        ]);
    }, [setBreadcrumbs]);

    useRoutingValidation();

    useEffect(() => {
        if (pageCategory) {
            updateSelectMessageCategory(pageCategory);
        }
    }, [pageCategory]);

    useEffect(() => {
        const getBatchZipCodes = async () => {
            const zipCodeResponse = await getBatchPrintZipCodes(appState?.territory?.id, appState?.category?.messageTypeId, appState?.currentProduct?.clientProductId);
            setZipCodes(zipCodeResponse.map(m => ({ value: m.zipCode, label: m.zipCode })));
        }

        getBatchZipCodes().catch(error => { showBoundary(error) });
    }, [appState.territoryId, appState.category.messageTypeId, appState.currentProduct.clientProductId])


    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchInput(searchInput.trim());
        }, 400);

        return () => clearTimeout(timeoutId);
    }, [searchInput]);

    useEffect(() => {
        const getHCPsBatch = async () => {
            const hcpsBatch = await getBatchPrintHCPs(appState?.category?.messageTypeId, appState?.currentProduct?.clientProductId, appState?.territory?.id, selectedZipCodes.map(m => (m.value)), debouncedSearchInput);
            setHcpsBatchResponses(hcpsBatch);
            setFilteredBatchRows(hcpsBatch.rows);
        }
        //prevent call when the messageTypeId is not set
        if (isDefinedNumber(appState.category.messageTypeId)) {
            getHCPsBatch().catch(error => { showBoundary(error) });
        }

    }, [appState.territory.id, appState.category.messageTypeId, appState.currentProduct.clientProductId, selectedZipCodes, debouncedSearchInput])

    useEffect(() => {
        const getPrescriberBatch = async (rowData) => {
            const prescriberBatch = await getBatchPrintPrescriber(rowData.type, rowData.messageCode, rowData.id, appState?.currentProduct?.clientProductId, appState?.category?.messageTypeId, appState?.territory?.id)
            setPrescriberData(prescriberBatch.dynamicTable);
            if (prescriberBatch.dynamicTable) {
                //pre select the columns to the max number of visible columns defined                               
                const visibleColumns = [];
                for (const header of prescriberBatch.dynamicTable.headers) {	
                    if((header.isRequired || header.autoSelect) && visibleColumns.length < maxPrintColumns){
                        visibleColumns.push(header.key);
                    }
                }
                setSelectedColumnState(visibleColumns);
                setExceedMaxColumns(prescriberBatch.dynamicTable?.headers.length > maxDisplayColumns);
            }
        }

        let row = [];
        if (hcpsBatchResponses) {
            row = hcpsBatchResponses.rows.map(d => d.data)[0];

            if (row) {
                getPrescriberBatch(row).catch(error => { showBoundary(error) });
            }
        }

    }, [hcpsBatchResponses, appState.territory.id, appState.category.messageTypeId, appState.currentProduct.clientProductId])

    useEffect(() => {
        if (appState?.config?.PlanList?.MaxDisplayColumns) {
            setMaxDisplayColumns(parseInt(getConfigValue(appState.config.PlanList.MaxDisplayColumns, appState)?.Value));
        }
    }, [appState]);


    useEffect(() => {
        if (isLoading) {
            setFeedbackMessage(getConfigValue(appState.config.Common?.Loading, appState)?.Value);
        } else if (noResultsFound) {
            setFeedbackMessage(getConfigValue(appState.config.Common?.NoResultsFound, appState)?.Value);
        } else {
            setFeedbackMessage('');
        }
    }, [isLoading, noResultsFound, appState]);

    useEffect(() => {
        if (!hcpsBatchResponses) {
            return;
        }
        let results = hcpsBatchResponses?.rows;
        if (hcpsBatchResponses?.rows?.length === maxRecords) {
            setMaxRecordsMessage(Mustache.render(getConfigValue(appState?.config?.BatchPrint?.MaxRecordsMessage, appState)?.Value, batchPrintConfig));
        } else {
            setMaxRecordsMessage(null);
        }
        if (groupPracticeEnabled) {
            results = results.filter(row => (row.data.type === hcpType.Prescriber && hcpCheckbox) || (row.data.type === hcpType.GroupPractice && groupPracticeCheckbox));
        }
        if (hcpFavoritesEnabled && hcpFavoriteCheckbox) {
            results = results.filter(row => row.data.favorite);
        }
        setFilteredBatchRows(results);
        setNoResultsFound(results.length === 0);
        if (!isFavoriteUpdateRef.current) {
            setBatchPageResetTrigger(prev => prev + 1);
        }
        isFavoriteUpdateRef.current = false;
    }, [hcpsBatchResponses, hcpCheckbox, groupPracticeCheckbox, hcpFavoriteCheckbox])

    useEffect(() => {
        // only propogate the changes when the print option is turned on (true).
        if (configTargetNameisChecked) {
            selectedBatch.forEach((item) => item.includeTargetName = true);
        }
    }, [configTargetNameisChecked, selectedBatch]);

    const updateHcps = function (prevState, hcp) {
        return prevState.map((p) => {
            if (p.data.id === hcp.id) {
                return { ...p, data: { ...p.data, favorite: !hcp.favorite } };
            }
            return p;
        });
    };

    function handleFavoriteClick(hcp) {
        isFavoriteUpdateRef.current = true;
        setHcpsBatchResponses((prevState) => {
            return { ...prevState, rows: updateHcps(prevState.rows, hcp) };
        });
        const target = selectedBatch.find((t) => t.data.id === hcp.id);
        if(target) {
            handleSelectedFavoriteClick(hcp);
        }
    }

    function handleSelectedFavoriteClick(hcp) {
        setSelectedBatch((prevState) => {
            return updateHcps(prevState, hcp);
        });
    }

    function updateShowTargetModal(show, refresh) {
        setShowTargetModal(show);
        if (refresh) {
            setIsLoading(true);
            getBatchPrintHCPs(appState?.category?.messageTypeId, appState?.currentProduct?.clientProductId, appState?.territory?.id, selectedZipCodes.map(m => (m.value)), debouncedSearchInput)
                .then((hcpsBatch) => {
                    setHcpsBatchResponses(hcpsBatch);
                    setFilteredBatchRows(hcpsBatch.rows);
                })
                .catch((error) => { showBoundary(error); })
                .finally(() => { setIsLoading(false); });
        }

    }


    function handleHcpCheckBoxClick(event) {
        setHcpCheckbox(event.target.checked);
    }

    function handleGroupPracticeCheckBoxClick(event) {
        setGroupPracticeCheckbox(event.target.checked)
    }

    function handleRemoveClick(item, event) {
        const isClicked = event.target;

        if (isLoading) {
            event.preventDefault();
            return;
        }

        if (isClicked) {
            item.includeTargetName = false;
            setSelectedBatch((originalBatch) => originalBatch.filter((d) => d.data.id !== item.data.id));
        }
    }

    function handleAddClick(data, event) {
        const isClicked = event.target;
        const maxReached = selectedBatch.length >= maxSelectedBatch;

        if (isLoading) {
            event.preventDefault();
            return false;
        }

        if (isClicked && !maxReached) {
            data.includeTargetName = configTargetNameisChecked;
            setSelectedBatch((originalBatch) => [...originalBatch, data]);
        }

        if (isClicked && maxReached) {
            event.preventDefault();
            showMaxTargets();
            return false;
        }

        return true;
    }

    function handleZipCode(zipCodes) {
        setSelectedZipCodes(zipCodes);
    }

    function handleChange(event) {
        setSearchInput(event.target.value)
    }

    function triggerToastAlert(title, message, autohide) {
        setToastTitle(title);
        setToastMessage(message);
        setShowToast(true);
        setToastAutohide(autohide);
    }

    function showMaxTargets() {
        const errorMessage = getConfigValue(appState.config?.BatchPrint?.Page?.MaxPrintableTargets?.Body, appState)?.Value;
        triggerToastAlert(
            getConfigValue(appState.config?.BatchPrint?.Page?.MaxPrintableTargets?.Title, appState)?.Value,
            Mustache.render(errorMessage, batchPrintConfig),
            false
        );
    }

    function updateSelectMessageCategory(pageCategory) {
        setAppState({
            ...appState,
            category: pageCategory,
        });
    }

    function onDetailsCategoryClick(category) {
        setPageCategory(category);
        setShowMessageDetailsModal(false);
        updateSelectMessageCategory(category);
        setHcpsBatchResponses(null);
        setSelectedBatch([]);
    }

    function onDetailsClick() {
        setShowMessageDetailsModal(true);
    }

    function onProductClick(product) {
        setAppState({ ...appState, currentProduct: product });
        setCurrentProduct(product);
        setShowProductsModal(false);
        setProductImageSource(product.imagePath)
        navigate('/MessageSelectionPage');
    }

    function handleBackButton() {
        navigate('/MessageSelectionPage');
    }

    function closeOpenMenus(event) {
        // click outside of menus and content menus = close menus
        if (configMenuRef.current && !configMenuRef.current.contains(event.target) && !configMenuContentRef.current.contains(event.target) && isConfigMenuVisible) {
            setIsConfigMenuVisible(false);
        }
    }

    function onOptionsClick(item) {
        const toInclude = item.includeTargetName;
        const target = selectedBatch.find((t) => t.data.id === item.data.id);
        if (!toInclude) {
            setConfigTargetNameisChecked(false);
        }
        if (!target && toInclude) {
            if (selectedBatch.length >= maxSelectedBatch) {
                showMaxTargets();
                return false;
            }
            setSelectedBatch((originalPlans) => [...originalPlans, item]);
            return true;
        }
        setSelectedBatch((originalPlans) => [...originalPlans.filter((t) => t.data.id !== target.data.id), item]);
        return true;

    }

    async function loadDoc(generationType) {
        setIsLoading(true);

        const props = {
            productId: appState.currentProduct.clientProductId,
            marketId: appState.market.id,
            territoryId: appState.territory.id,
            addEHRBackPage: configEHRisChecked,
            addCoverPage: printOptionsCoverPageIsChecked,
            generateFor: generationType,
            messageTypeId: appState.category.messageTypeId,
            columnKeys: selectedColumnState,
            improvedAccessPlan: null,
            includeSalesRep: printOptionsSalesRepInfoIsChecked,
            includeFRM: printOptionsFRMInfoInfoIsChecked,
            hcps: selectedBatch.map((item) => {
                return {
                    printHCPName: item.includeTargetName,
                    level: item.data.type,
                    hcpId: item.data.id,
                    messageCode: item.data.messageCode
                }
            }),
            includeSavingsCard: savingsCardIsChecked,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            purchaseOrderNumber: appState.purchaseOrderNumber
        };

        const response = await batchPrintDocumentGenerate(props)
            .then((responseJson) => {
                setIsLoading(false);

                // store the properties of the last document generated
                setAppState({
                    ...appState,
                    lastGeneratedDocument: props,
                    sellSheetId: responseJson.sellSheetId,
                    purchaseOrderNumber: responseJson.purchaseOrderNumber
                });

                return responseJson;
            })
            .catch(err => {
                setShowPreviewModal(false);
                setIsLoading(false);
                triggerToastAlert("Error", "There was an error generating the document. Please try again.", true);
                console.error(err)
            });
        return response;
    }

    async function onPreviewClick() {
        setPdfUrl(null);
        setShowPreviewModal(true);
        const response = await loadDoc(documentGenerationType.Preview);
        const url = URL.createObjectURL(response.blob);
        setPdfFileName(response.fileName);
        setPdfUrl(url);
    }

    async function onGenerateClick() {
        const response = await loadDoc(documentGenerationType.Generate);
        const url = URL.createObjectURL(response.blob);
        navigate('/GenerateDocumentPage', { state: { pdfURL: url, fileName: response.fileName } });
    }

    document.addEventListener('mousedown', closeOpenMenus);

    return (
        <div>
            <CreateSalesAidStepProgressBar currentStepId={SalesAidStepIds.SELECTTARGET} focusType={focusTypeName.Messages} />

            <div
                className="container-fluid pt-3 pt-md-4 pb-1 color-bg-bodybg z-2"
                id="plan-list-header"
            >
                <div className="container-xl">
                    <div className='row d-flex align-items-md-center mb-2 mb-lg-4'>
                        <div className='col-12 col-md-6 order-2 order-md-1'>
                            <h1 className='type-20 color-grey-d1 mt-4 mt-md-0 hide-sticky'>
                                {getConfigValue(appState.config?.BatchPrint?.Page?.SelectTarget, appState)?.Value}
                            </h1>
                            <div className="row justify-content-end show-sticky-only">
                                <div className="row justify-content-start">
                                    <div className="col-6 col-md-auto type-14 text-md-start"><div className="color-grey-d1">{getConfigValue(appState.config?.PlanList?.ProductLabel, appState)?.Value}</div>{appState.currentProduct.name}</div>
                                </div>
                            </div>
                        </div>
                        <div className='col-12 col-md-6 order-1 order-md-2'>
                            <div className='row justify-content-end'>
                                <div className='col-6 col-md-auto type-14 text-md-end'>
                                </div>
                                <div className='col-6 col-md-auto type-14 text-md-end'>
                                    <div className='color-grey-d1'>
                                        {getConfigValue(appState.config?.PlanList?.TerritoryLabel, appState)?.Value}
                                    </div>
                                    {appState.territory?.name}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="my-8 d-none" id="plan-list-header-spacer"></div>


            <div className="container-xl">
                <div className="row d-flex mb-5">
                    {/* This section will be replaced with the config data to pull in the brands */}
                    <div className="col-12 col-md-4">
                        <ProductsModalButton
                            setShowProductsModal={setShowProductsModal}
                            productImageSource={productImageSource}
                            productName={currentProduct?.productName}
                        ></ProductsModalButton>
                    </div>

                    <div className='col-12 col-md-4 mt-4 mt-md-0'>
                        <CategoryDropdown
                            pageCategory={pageCategory}
                            onDetailsClick={onDetailsClick}
                            isLoading={isLoading}
                        />
                    </div>
                    {hcpsBatchResponses &&
                        <div className="col-12 col-md-4 mt-4 mt-md-0">
                            <PrintOptionDropdown
                                isConfigMenuVisible={isConfigMenuVisible}
                                setIsConfigMenuVisible={setIsConfigMenuVisible}
                                configMenuContentRef={configMenuContentRef}
                                configMenuRef={configMenuRef}
                                configHCPisChecked={configTargetNameisChecked}
                                setConfigHCPisChecked={setConfigTargetNameisChecked}
                                configEHRisChecked={configEHRisChecked}
                                setConfigEHRisChecked={setConfigEHRisChecked}
                                isLoading={isLoading}
                                selectedColumnState={selectedColumnState}
                                setSelectedColumnState={setSelectedColumnState}
                                isDisabled={false}
                                printOptionsEhrBackPageEnabled={printOptionsEhrBackPageEnabled}
                                printOptionsCoverPageEnabled={printOptionsCoverPageEnabled}
                                printOptionsHcpNameEnabled={false}
                                printOptionsLocationNameEnabled={false}
                                printConfigColumnHeaders={prescriberData.headers}
                                savingsCardIsChecked={savingsCardIsChecked}
                                setSavingsCardIschecked={setSavingsCardIsChecked}
                                savingsCardIsEnabled={savingsCardIsEnabled}
                                configCoverisChecked={printOptionsCoverPageIsChecked}
                                setConfigCoverisChecked={setPrintOptionsCoverPageIsChecked}
                                canSelectSavingsCard={true}
                                canSelectSalesRepNameInfo={printOptionsSelectSalesRepInfoEnabled}
                                canSelectFRMInfo={printOptionsSelectFRMInfoEnabled}
                                printSalesRepoInfoIsChecked={printOptionsSalesRepInfoIsChecked}
                                printFRMInfoIsChecked={printOptionsFRMInfoInfoIsChecked}
                                setPrintSalesRepoInfoIsChecked={setPrintOptionsSalesRepInfoIsChecked}
                                setPrintFRMInfoIsChecked={setPrintOptionsFRMInfoInfoIsChecked}
                                hcpNameRequired={hcpNameRequired}
                                ehrBackPageRequired={ehrBackPageRequired}
                                coverPageRequired={batchCoverPageRequired}
                                savingsCardRequired={savingsCardRequired}
                                salesRepInfoRequired={salesRepInfoRequired}
                                frmInfoRequired={frmInfoRequired}
                            />
                        </div>
                    }
                </div>


                {/* <!-- Filters --> */}

                <div className="row mt-5 mb-3 mb-lg-4 align-items-center">
                    <div className="col-12 col-md-5">
                        <div className="circle-singleline text-bg-info rounded-circle type-18 me-2 text-white">{selectedBatch.length}</div>{getConfigValue(appState.config?.BatchPrint?.Page?.TargetBundle, appState)?.Value}
                        <button className="btn-ghost-primary bg-transparent"
                            data-bs-toggle="modal"
                            data-bs-target="#targets_selected"
                            onClick={() => setShowTargetModal(true)}
                            disabled={selectedBatch.length === 0}
                            type="button">
                            {getConfigValue(appState.config?.BatchPrint?.Page?.Review, appState)?.Value}
                        </button>
                    </div>
                    <div className="col-12 col-md-7">
                        <h3 className="type-24 color-grey-d3 d-md-none my-4">{getConfigValue(appState.config?.BatchPrint?.Page?.Filters, appState)?.Value}</h3>
                        <div className="d-flex flex-column flex-md-row justify-content-md-end">
                            <FavoriteFilter
                                hcpFavoriteCheckbox={hcpFavoriteCheckbox}
                                setHcpFavoriteCheckbox={setHcpFavoriteCheckbox}
                                includeWrappingRow={false}
                            />
                            {groupPracticeEnabled &&
                                <>
                                    < div className="form-check mb-3 mb-md-0 me-md-5">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="list-filter-hcps"
                                            checked={hcpCheckbox}
                                            onChange={(event) => handleHcpCheckBoxClick(event)}>
                                        </input>
                                        <label className="form-check-label type-14" htmlFor="list-filter-hcps">{getConfigValue(appState.config?.BatchPrint?.Page?.HcpCheckBox, appState)?.Value}</label>
                                    </div>

                                    <div className="form-check mb-3 mb-md-0">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="list-filter-groups"
                                            checked={groupPracticeCheckbox}
                                            onChange={(event) => handleGroupPracticeCheckBoxClick(event)}>
                                        </input>
                                        <label className="form-check-label type-14" htmlFor="list-filter-groups">{getConfigValue(appState.config?.BatchPrint?.Page?.GroupPracticeCheckbox, appState)?.Value}</label>
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
                {maxRecordsMessage && (
                    <div className="row">
                        <div className="col-12 mb-3 mb-md-0">
                            <div className="alert alert-primary" role="alert">
                                <div className="alert-content">
                                <p>{maxRecordsMessage}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className="col-12 col-md-6 mb-3 mb-md-0">
                        <Select
                            unstyled
                            isMulti
                            type="search"
                            classNames={{
                                control: () => 'form-control color-grey-d1',
                                menuList: () => 'card',
                                option: (state) =>
                                    state.isFocused ? 'bg-color-precision-grey text-white p-2' : 'p-2',
                            }}
                            styles={{
                                menu: (base) => ({ ...base, zIndex: 2 })
                            }}
                            placeholder={getConfigValue(appState.config?.BatchPrint?.Page?.ZipPlaceholder, appState)?.Value}
                            onChange={handleZipCode}
                            options={zipCodes}
                            value={selectedZipCodes}
                        >
                        </Select>
                    </div>
                    <div className="col-12 col-md-6">
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-control icon-search"
                                placeholder={getConfigValue(appState.config?.BatchPrint?.Page?.SearchPlaceholder, appState)?.Value}
                                value={searchInput}
                                onChange={handleChange}>
                            </input>
                        </div>
                    </div>
                </div>
            </div>
            {
                hcpsBatchResponses && !feedbackMessage &&
                <BatchPrintList
                    headers={hcpsBatchResponses.headers}
                    batchRows={filteredBatchRows}
                    selectedBatch={selectedBatch}
                    handleAddClick={handleAddClick}
                    handleRemoveClick={handleRemoveClick}
                    onOptionsClick={onOptionsClick}
                    showBackButton={false}
                    updateTargetRecords={handleFavoriteClick}
                    displayFavoriteDesignation={true}
                    pageResetTrigger={batchPageResetTrigger}
                >
                </BatchPrintList>
            }
            {
                feedbackMessage ? (
                    <div className="container-xl mt-5 mb-5 mb-lg-7">
                        <div className='row pd-plan-table-row pd-plan-table-card align-items-center'>
                            <div className='color-grey-d1 py-4'>{feedbackMessage}</div>
                        </div>
                    </div>
                ) : null
            }

            <div className='button-bottom-container-sticky-low-z'>
                <div className='container-xl'>
                    <div className='row py-3'>
                        <div className='col-5'>
                            <div className='d-flex flex-column flex-lg-row'>
                                <button
                                    onClick={onPreviewClick}
                                    disabled={!isValid || isLoading}
                                    className={`btn btn-outline-primary btn-lg flex-fill w-100 ${isValid || isLoading ? "" : "disabled"}`}
                                >
                                    {getConfigValue(appState.config?.PlanList?.Preview.Label, appState)?.Value || null}
                                </button>
                                <div className='flex-fill w-100 ms-lg-3 pe-3'></div>
                            </div>
                        </div>
                        <div className='col-7'>
                            <div className='d-flex flex-md-row-reverse'>
                                <button
                                    className={`btn btn-success btn-lg mb-lg-0 ms-lg-3 flex-fill w-100 ml-1 ${isValid || isLoading ? "" : "disabled"} ${isLoading ? "btn-working" : ""}`}
                                    type="button"
                                    disabled={!isValid || isLoading}
                                    onClick={onGenerateClick}
                                >
                                    {' '}{getConfigValue(appState.config?.PlanList?.Generate?.Label, appState)?.Value || null}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleBackButton}
                                    className="btn btn-secondary btn-lg flex-fill w-100">
                                    {getConfigValue(appState.config?.Common?.Back, appState)?.Value || null}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ToastAlert show={showToast} setShow={setShowToast} title={toastTitle} message={toastMessage} autohide={toastAutohide} />

            <MessageDetailsModal
                show={showMessageDetailsModal}
                setShow={setShowMessageDetailsModal}
                categories={batchPrintCategories}
                onDetailsCategoryClick={onDetailsCategoryClick}
            />

            <ProductsModal
                show={showProductsModal}
                setShow={setShowProductsModal}
                onProductClick={onProductClick}
                onlySellSheetProducts={true}
            />

            <PreviewModal
                show={showPreviewModal}
                setShow={setShowPreviewModal}
                onGenerateClick={onGenerateClick}
                isValid={isValid}
                isLoading={isLoading}
                pdfUrl={pdfUrl}
                showGenerateBack={true}
                showPrintDownload={false}
                pdfFileName={pdfFileName}
                supportsProfessionalPrinting={true}
                skipPreviewTrack={true}
                returnURL={"/SelectTargetBatchPage"}
            />

            {
                hcpsBatchResponses &&
                <TargetModal
                    show={showTargetModal}
                    setShow={updateShowTargetModal}
                    selectedBatch={selectedBatch}
                    headers={hcpsBatchResponses.headers}
                    handleAddClick={handleAddClick}
                    handleRemoveClick={handleRemoveClick}
                    onOptionsClick={onOptionsClick}
                    updateTargetRecords={handleSelectedFavoriteClick}
                />
            }
        </div >

    );
}

export default SelectTargetBatchPage


